import React from 'react';
import { Box, Button, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircle as ApproveIcon,
  Delete as DeleteIcon,
  AutoAwesome as AiIcon,
} from '@mui/icons-material';

export interface MappingTableToolbarProps {
  selectedCount: number;
  onApproveSelected?: () => void;
  onDeleteSelected?: () => void;
  onAiRegenSelected?: () => void;
  onDeselectAll?: () => void;
}

/**
 * Phase 2 (P1): Bulk operations toolbar shown when rows are selected
 * in the mapping table view.
 */
const MappingTableToolbar: React.FC<MappingTableToolbarProps> = ({
  selectedCount,
  onApproveSelected,
  onDeleteSelected,
  onAiRegenSelected,
  onDeselectAll,
}) => {
  if (selectedCount === 0) return null;

  return (
    <Box
      sx={{
        px: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: alpha('#2563eb', 0.06),
        borderBottom: '1px solid',
        borderColor: alpha('#2563eb', 0.15),
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2563eb', fontSize: 13, mr: 1 }}>
        {selectedCount} selected
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Stack direction="row" spacing={0.75}>
        <Tooltip title="Approve selected mappings">
          <span>
            <Button
              size="small"
              startIcon={<ApproveIcon sx={{ fontSize: 16 }} />}
              onClick={onApproveSelected}
              disabled={!onApproveSelected}
              sx={{ fontSize: 12, textTransform: 'none', fontWeight: 600 }}
            >
              Approve
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="AI regenerate selected mappings">
          <span>
            <Button
              size="small"
              startIcon={<AiIcon sx={{ fontSize: 16 }} />}
              onClick={onAiRegenSelected}
              disabled={!onAiRegenSelected}
              sx={{ fontSize: 12, textTransform: 'none', fontWeight: 600 }}
            >
              AI Regen
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Delete selected mappings">
          <span>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              onClick={onDeleteSelected}
              disabled={!onDeleteSelected}
              sx={{ fontSize: 12, textTransform: 'none', fontWeight: 600 }}
            >
              Delete
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{ ml: 'auto' }}>
        <Button
          size="small"
          onClick={onDeselectAll}
          sx={{ fontSize: 11, textTransform: 'none', color: 'text.secondary' }}
        >
          Clear selection
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(MappingTableToolbar);
